import { Box, CircularProgress, IconButton, TextField } from '@mui/material';
import React from 'react';
import {
  Maybe,
  MeasurementHistory,
  Module,
  useMeasurementsHistoryQuery,
} from '../graphql/codegen/graphql-react';
import {
  getDefaultHistoryDateRange,
  getHistoryDateRangeFromStore,
  setHistoryDateRangeInStore,
} from '../helpers/historyDateRangeStore';
import {
  capitalize,
  dayjs,
  EXTRACT_LIMIT_DAYS,
  getErrorMessage,
  logErr,
  MEASUREMENTS_HISTORY_DEFAULT_RANGE_DAYS,
} from '../helpers/utils';
import ErrorMessage from './ErrorMessage';
import DeviceDetailTable from './tables/DeviceDetailTable';
import Center from './ui/Center';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSetState } from 'react-use';
import { ls } from '../i18n/translations';
import Flex from './ui/Flex';
import { useMQ } from '../hook/useMQ';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { colors } from '../theme';
import { ModuleSettingType } from '../graphql/dataTypes';
import { useDarkMode } from '../hook/useDarkMode';

interface TableDetailProps {
  module: Maybe<Module>;
}

const TableDetail: React.FC<TableDetailProps> = ({ module }) => {
  const initialDateRange = React.useMemo(
    () => getHistoryDateRangeFromStore(module?.id) ?? getDefaultHistoryDateRange(),
    [module?.id],
  );
  const [state, setState] = useSetState<{
    from: dayjs.Dayjs | null;
    to: dayjs.Dayjs | null;
    isRefetching: boolean;
  }>({
    from: initialDateRange.from,
    to: initialDateRange.to,
    isRefetching: false,
  });
  React.useEffect(() => {
    if (!module?.id) return;
    const next = getHistoryDateRangeFromStore(module.id) ?? getDefaultHistoryDateRange();
    setState({ from: next.from, to: next.to });
  }, [module?.id, setState]);
  const {
    loading: measurementHistoryLoading,
    error: measurementHistoryError,
    data: measurementHistoryData,
    refetch: measurementHistoryRefetch,
  } = useMeasurementsHistoryQuery({
    skip: !module?.id,
    variables: {
      input: {
        moduleId: module?.id ?? '',
        from: state.from?.toDate(),
        to: state.to?.toDate(),
      },
    },
  });
  const { isMobile, isMobileOrTablet } = useMQ();
  const { isDarkMode } = useDarkMode();

  if (!module) {
    return null;
  }

  if (measurementHistoryLoading) {
    return (
      <Center mt={2} mb={6}>
        <CircularProgress />
      </Center>
    );
  }

  const moduleSettings = (module.ModuleSettings?.settings || []) as ModuleSettingType[];
  const measurementsHistoryData = (measurementHistoryData?.measurementsHistory ||
    []) as MeasurementHistory[];

  return (
    <Box my={2}>
      <ErrorMessage
        containerStyle={{ paddingBottom: 2, textAlign: 'center' }}
        message={getErrorMessage(measurementHistoryError)}
      />
      <Flex alignItems='flex-start' mt={0.5}>
        <Flex
          ml={2}
          justifyContent={isMobileOrTablet ? 'center' : 'flex-start'}
          alignItems={isMobileOrTablet ? 'flex-start' : 'center'}
          flexDirection={isMobileOrTablet ? 'column' : 'row'}
        >
          <Box>
            <DatePicker
              renderInput={(props) => <TextField size='small' {...props} />}
              label={capitalize(ls.from)}
              value={state.from}
              onChange={(newValue) => {
                setState((prev) => {
                  if (!newValue) {
                    setHistoryDateRangeInStore(module?.id, null, null);
                    return { from: null };
                  }
                  const endToday = dayjs().endOf('day');
                  let nextTo = prev.to;
                  if (!nextTo || newValue.isAfter(nextTo, 'day')) {
                    const candidate = newValue
                      .add(MEASUREMENTS_HISTORY_DEFAULT_RANGE_DAYS - 1, 'day')
                      .endOf('day');
                    nextTo = candidate.isAfter(endToday) ? endToday : candidate;
                  } else {
                    const span =
                      nextTo.startOf('day').diff(newValue.startOf('day'), 'day') + 1;
                    if (span > EXTRACT_LIMIT_DAYS) {
                      const capped = newValue
                        .add(EXTRACT_LIMIT_DAYS - 1, 'day')
                        .endOf('day');
                      nextTo = capped.isAfter(endToday) ? endToday : capped;
                    }
                  }
                  setHistoryDateRangeInStore(module?.id, newValue, nextTo);
                  return { from: newValue, to: nextTo };
                });
              }}
              disableFuture
              inputFormat='YYYY/MM/DD'
              InputProps={{
                style: {
                  fontSize: '1.1rem',
                },
              }}
            />
          </Box>
          <Box mt={isMobileOrTablet ? 1 : 0} ml={isMobileOrTablet ? 0 : 4}>
            <DatePicker
              renderInput={(props) => <TextField size='small' {...props} />}
              label={capitalize(ls.to)}
              value={state.to}
              minDate={state.from ?? undefined}
              shouldDisableDate={(d) => {
                if (!state.from) return false;
                return (
                  dayjs(d).startOf('day').diff(state.from.startOf('day'), 'day') >=
                  EXTRACT_LIMIT_DAYS
                );
              }}
              onChange={(newValue) => {
                setState((prev) => {
                  let nextFrom = prev.from;
                  if (newValue && prev.from) {
                    const span = newValue.startOf('day').diff(prev.from.startOf('day'), 'day') + 1;
                    if (span > EXTRACT_LIMIT_DAYS) {
                      nextFrom = newValue.subtract(EXTRACT_LIMIT_DAYS - 1, 'day').startOf('day');
                    }
                  }
                  if (newValue && nextFrom) {
                    setHistoryDateRangeInStore(module?.id, nextFrom, newValue);
                  } else {
                    setHistoryDateRangeInStore(module?.id, null, null);
                  }
                  return { to: newValue, from: nextFrom };
                });
              }}
              inputFormat='YYYY/MM/DD'
              disableFuture
              InputProps={{
                style: {
                  fontSize: '1.1rem',
                },
              }}
            />
          </Box>
        </Flex>
        <Box mx={2}>
          <IconButton
            disabled={state.isRefetching}
            onClick={async () => {
              try {
                setState({ isRefetching: true });
                await measurementHistoryRefetch({
                  input: {
                    moduleId: module?.id ?? '',
                    from: state.from ? state.from.toDate() : undefined,
                    to: state.to ? state.to.toDate() : undefined,
                  },
                });
              } catch (error) {
                logErr('error on refetch measurementHistory', error);
              } finally {
                setState({ isRefetching: false });
              }
            }}
          >
            {state.isRefetching ? (
              <Center height={22}>
                <CircularProgress size={20} />
              </Center>
            ) : (
              <Center height={22}>
                <RefreshIcon style={{ color: isDarkMode ? colors.lightBlue : colors.blue }} />
              </Center>
            )}
          </IconButton>
        </Box>
      </Flex>
      <Box mt={isMobile ? 2 : 0}>
        <DeviceDetailTable
          data={measurementsHistoryData}
          moduleSettings={moduleSettings}
          historyRangeFrom={state.from}
          historyRangeTo={state.to}
        />
      </Box>
    </Box>
  );
};

export default TableDetail;
